
import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { departments } from '@/utils/mockData';
import { toast } from 'sonner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const Settings: React.FC = () => {
  const [companyName, setCompanyName] = useState('Mijn Bedrijf');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [automaticScheduling, setAutomaticScheduling] = useState(false);
  const [departmentsList, setDepartmentsList] = useState(departments);
  const [newDepartmentName, setNewDepartmentName] = useState('');

  const handleSaveGeneral = () => {
    toast.success('Algemene instellingen opgeslagen');
  };

  const handleSaveNotifications = () => {
    toast.success('Notificatie instellingen opgeslagen');
  };

  const handleAddDepartment = () => {
    if (!newDepartmentName.trim()) {
      toast.error('Voer een geldige afdelingsnaam in');
      return;
    }
    
    const newId = (Math.max(...departmentsList.map(d => parseInt(d.id))) + 1).toString();
    const colors = ['#3B82F6', '#10B981', '#6366F1', '#F59E0B', '#EC4899', '#8B5CF6'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    
    setDepartmentsList([
      ...departmentsList,
      { id: newId, name: newDepartmentName, color: randomColor }
    ]);
    
    setNewDepartmentName('');
    toast.success(`Afdeling "${newDepartmentName}" toegevoegd`);
  };

  const handleRemoveDepartment = (id: string) => {
    setDepartmentsList(departmentsList.filter(dept => dept.id !== id));
    toast.success('Afdeling verwijderd');
  };

  return (
    <Layout>
      <PageHeader 
        title="Instellingen" 
        description="Beheer de configuratie van uw rooster-app"
      />
      
      <Tabs defaultValue="general">
        <TabsList className="mb-6">
          <TabsTrigger value="general">Algemeen</TabsTrigger>
          <TabsTrigger value="departments">Afdelingen</TabsTrigger>
          <TabsTrigger value="notifications">Notificaties</TabsTrigger>
        </TabsList>
        
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>Algemene Instellingen</CardTitle>
              <CardDescription>
                Pas de basisinstellingen van uw rooster-app aan
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="companyName">Bedrijfsnaam</Label>
                <Input
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>
              
              <Separator />
              
              <div className="space-y-3">
                <Label>Automatisch roosteren</Label>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="font-medium">AI-ondersteunde roostering</div>
                    <div className="text-sm text-muted-foreground">
                      Laat de app automatisch optimale diensten voorstellen
                    </div>
                  </div>
                  <Switch
                    checked={automaticScheduling}
                    onCheckedChange={setAutomaticScheduling}
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={handleSaveGeneral}>Opslaan</Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="departments">
          <Card>
            <CardHeader>
              <CardTitle>Afdelingen</CardTitle>
              <CardDescription>
                Beheer de afdelingen binnen uw organisatie
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Huidige afdelingen</Label>
                <div className="space-y-3">
                  {departmentsList.map(dept => (
                    <div key={dept.id} className="flex items-center justify-between p-2 border rounded-md">
                      <div className="flex items-center">
                        <div 
                          className="w-4 h-4 rounded-full mr-3" 
                          style={{ backgroundColor: dept.color }}
                        />
                        <span>{dept.name}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleRemoveDepartment(dept.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        Verwijderen
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label htmlFor="newDepartment">Nieuwe afdeling toevoegen</Label>
                <div className="flex space-x-2">
                  <Input
                    id="newDepartment"
                    placeholder="Afdelingsnaam"
                    value={newDepartmentName}
                    onChange={(e) => setNewDepartmentName(e.target.value)}
                  />
                  <Button onClick={handleAddDepartment}>Toevoegen</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notificatie Instellingen</CardTitle>
              <CardDescription>
                Configureer hoe en wanneer u notificaties wilt ontvangen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="font-medium">E-mail notificaties</div>
                    <div className="text-sm text-muted-foreground">
                      Ontvang roosterupdates via e-mail
                    </div>
                  </div>
                  <Switch
                    checked={emailNotifications}
                    onCheckedChange={setEmailNotifications}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="font-medium">Push notificaties</div>
                    <div className="text-sm text-muted-foreground">
                      Ontvang meldingen op uw apparaat
                    </div>
                  </div>
                  <Switch
                    checked={pushNotifications}
                    onCheckedChange={setPushNotifications}
                  />
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label className="font-medium">Gebeurtenissen</Label>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span>Rooster gepubliceerd</span>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Dienst gewijzigd</span>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Verlofaanvraag</span>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Dienstruil verzoek</span>
                    <Switch defaultChecked />
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={handleSaveNotifications}>Opslaan</Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </Layout>
  );
};

export default Settings;
